import { Box, Typography } from '@mui/material';
import { Download, RefreshCw, X } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { createAdminApi, roleSatisfies, type AdminAuditEvent } from '@/admin/adminApi';
import { hasNextPage } from '@/admin/users/accountPresentation';
import { messageOf } from '@/admin/users/adminErrors';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { useHttpClient } from '@/app/providers';
import {
  AccessDenied,
  Avatar,
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  LoadingState,
  MasterDetail,
  PageHeader,
  SelectField,
  Switch,
  useDetailRoute,
  visuallyHidden,
  type DataTableColumn,
} from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import { AuditEventDetail } from './AuditEventDetail';
import { exportIsTruncated, saveFile } from './auditExport';
import {
  DEFAULT_FILTERS,
  PERIOD_OPTIONS,
  hasActiveFilter,
  isoToLocalInput,
  localInputToIso,
  readFilters,
  readPage,
  toQuery,
  writeFilters,
  type AuditFilters,
  type AuditPeriod,
} from './auditFilters';
import {
  ACTION_OPTIONS,
  TARGET_TYPE_OPTIONS,
  actionLabel,
  describeActor,
  describeAuditPage,
  describeTarget,
  formatCompact,
  isKnownAction,
  reasonOf,
  shortId,
  targetTypeLabel,
} from './auditPresentation';
import { AUDIT_PATH } from './auditRoutes';
import { useAuditEvents } from './useAuditEvents';
import { useReloadOnReturn } from './useReloadOnReturn';

/**
 * Journal d'audit (`/compliance/audit`, maquette P14).
 *
 * C'est la contrepartie de l'acces transverse (ADR 0032) : tout acces
 * d'administration y est ecrit, consultation comprise. Cinq partis pris :
 *
 * 1. **Le filtrage est celui du serveur**, et le total affiche est le sien
 *    (`COUNT(*)`, filtres compris). Aucune ligne n'est masquee ni comptee ici.
 * 2. **Les filtres vivent dans l'URL** : ils survivent a l'ouverture du tiroir,
 *    a un rechargement et a un lien partage.
 * 3. **Acteur, cible et corrélation se choisissent depuis une ligne**, dans le
 *    tiroir : le serveur n'offre pas de liste ou les choisir.
 * 4. **« Masquer les consultations » est un geste, pas un defaut** : la lecture
 *    du journal s'y inscrit elle-meme, et deux lectures successives different.
 *    Masquer les consultations rend le journal lisible ; cacher par defaut ce
 *    que ce registre existe pour montrer ne serait pas honnete.
 * 5. **Un intervalle inverse est une erreur dite**, jamais une liste vide : une
 *    liste vide se lit « il ne s'est rien passe ».
 *
 * Le journal est en lecture seule, y compris pour un administrateur : aucun
 * geste ici ne l'ecrit, et il n'en existera pas.
 */

const PER_PAGE = 25;

/** Plusieurs saisies produisent ce refus (UUID illisible, paramètre répété) : l'intervalle n'en est que l'exemple courant. */
const INVALID_FILTERS_MESSAGE =
  'Le serveur a refusé ces filtres, par exemple une date de début postérieure à la date de fin. Vérifiez-les, puis réessayez.';

/**
 * Valeur de l'option « aucun filtre » des listes deroulantes. Une option a
 * valeur vide ne s'afficherait pas une fois choisie (MUI laisse le champ blanc
 * sans `displayEmpty`) : l'ecran parle donc en `ANY`, et `''` reste la valeur
 * du filtre « absent » dans l'URL et la requete.
 */
const ANY = '*';

const toOption = (filter: string): string => (filter === '' ? ANY : filter);

const fromOption = (value: string): string => (value === ANY ? '' : value);

const withAny = (
  anyLabel: string,
  options: readonly { readonly value: string; readonly label: string }[],
  current: string,
  labelOf: (value: string) => string,
): { readonly value: string; readonly label: string }[] => [
  { value: ANY, label: anyLabel },
  ...options,
  // Un filtre venu d'un lien ou d'une ligne peut etre hors dictionnaire : il reste
  // selectionnable, affiche brut, plutot que de s'effacer en silence.
  ...(current === '' || options.some((option) => option.value === current)
    ? []
    : [{ value: current, label: labelOf(current) }]),
];

// ---------------------------------------------------------------------------
// Periode precise
// ---------------------------------------------------------------------------

/**
 * Deux dates et un bouton « Appliquer » : chaque requete d'administration
 * s'ecrit au journal, une requete par frappe de clavier le noierait.
 */
const CustomPeriod = ({
  from,
  to,
  onApply,
}: {
  readonly from: string;
  readonly to: string;
  readonly onApply: (from: string, to: string) => void;
}): ReactElement => {
  const [draftFrom, setDraftFrom] = useState(isoToLocalInput(from));
  const [draftTo, setDraftTo] = useState(isoToLocalInput(to));

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onApply(localInputToIso(draftFrom), localInputToIso(draftTo, true));
      }}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}
    >
      <Box sx={{ flex: '1 1 200px' }}>
        <Field label="Depuis" type="datetime-local" value={draftFrom} onChange={setDraftFrom} hint="Heure locale, à la minute." />
      </Box>
      <Box sx={{ flex: '1 1 200px' }}>
        <Field label="Jusqu’à" type="datetime-local" value={draftTo} onChange={setDraftTo} hint="Heure locale, minute incluse." />
      </Box>
      <Box sx={{ pb: { xs: 0, sm: 3 } }}>
        <Button type="submit" variant="primary">
          Appliquer les dates
        </Button>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Tableau
// ---------------------------------------------------------------------------

/** Valeur principale, puis son second rang en legende. */
const TwoLines = ({ main, secondary, mono = false }: { main: string; secondary: string | null; mono?: boolean }): ReactElement => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ overflowWrap: 'anywhere' }}>{main}</Box>
    {secondary === null ? null : (
      <Typography
        component="div"
        sx={{ fontSize: 12, color: 'tokens.textMuted', fontFamily: mono ? fontStacks.mono : undefined, overflowWrap: 'anywhere' }}
      >
        {secondary}
      </Typography>
    )}
  </Box>
);

const COLUMNS: readonly DataTableColumn<AdminAuditEvent>[] = [
  { key: 'when', header: 'Quand', render: (event) => formatCompact(event.occurredAt) },
  {
    key: 'actor',
    header: 'Acteur',
    render: (event) => {
      const actor = describeActor(event);

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar name={actor.label ?? '?'} tone={event.actorUserId === null ? 'yellow' : 'blue'} size={24} />
          <TwoLines main={actor.label ?? '—'} secondary={actor.secondary} mono />
        </Box>
      );
    },
  },
  {
    key: 'action',
    header: 'Action',
    primary: true,
    render: (event) => (
      <TwoLines
        main={actionLabel(event.action)}
        secondary={isKnownAction(event.action) ? event.action : null}
        mono
      />
    ),
  },
  {
    key: 'target',
    header: 'Cible',
    render: (event) => {
      const target = describeTarget(event);

      return target.label === null ? null : <TwoLines main={target.label} secondary={target.secondary} />;
    },
  },
  { key: 'reason', header: 'Motif', render: (event) => reasonOf(event.metadata) },
];

/**
 * Colonnes quand le tiroir est ouvert : la liste ne garde alors qu'environ la
 * moitie de la largeur, et six colonnes y casseraient les adresses au milieu.
 * Une seule colonne « Événement » porte l'essentiel — l'action, puis qui et sur
 * quoi —, le tiroir disant le reste (motif, identifiants, metadonnees).
 */
const COMPACT_COLUMNS: readonly DataTableColumn<AdminAuditEvent>[] = [
  { key: 'when', header: 'Quand', render: (event) => formatCompact(event.occurredAt) },
  {
    key: 'event',
    header: 'Événement',
    primary: true,
    render: (event) => {
      const actor = describeActor(event);
      const target = describeTarget(event);

      return (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 700 }}>{actionLabel(event.action)}</Box>
          <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
            {actor.label}
            {target.label === null ? '' : ` → ${target.label}`}
          </Typography>
        </Box>
      );
    },
  },
];

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

type ExportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'done'; readonly filename: string; readonly truncated: boolean }
  | { readonly kind: 'error'; readonly message: string };

const ExportStatus = ({ state }: { readonly state: ExportState }): ReactElement | null => {
  if (state.kind === 'idle') {
    return null;
  }

  if (state.kind === 'busy') {
    return <LoadingState label="Préparation de l’export…" />;
  }

  if (state.kind === 'error') {
    return (
      <Typography role="alert" sx={{ fontSize: 14, color: 'error.main' }}>
        L’export a échoué : {state.message}
      </Typography>
    );
  }

  return (
    <Typography role="status" sx={{ fontSize: 14 }}>
      Export téléchargé ({state.filename}).
      {state.truncated
        ? ' Le fichier est tronqué au plafond du serveur : affinez les filtres pour obtenir le reste.'
        : ''}
    </Typography>
  );
};

// ---------------------------------------------------------------------------
// Ecran
// ---------------------------------------------------------------------------

export const AuditLogScreen = (): ReactElement => {
  const http = useHttpClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { access, reload: reloadAccess } = useAdminAccess();
  const granted = access.kind === 'granted';
  const canExport = access.kind === 'granted' && roleSatisfies(access.role, 'admin');

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const page = readPage(searchParams);
  const detail = useDetailRoute(AUDIT_PATH);

  const [attempt, setAttempt] = useState(0);
  const [exportState, setExportState] = useState<ExportState>({ kind: 'idle' });

  const refresh = useCallback((): void => {
    setAttempt((previous) => previous + 1);
  }, []);

  // Seule relecture automatique (ADR 0035 §4) : au retour sur l'onglet.
  useReloadOnReturn(refresh);

  const { busy, page: result, error } = useAuditEvents({
    enabled: granted,
    filters,
    page,
    perPage: PER_PAGE,
    attempt,
  });

  /**
   * Change les filtres ou la page. Navigue vers la **base** : le tiroir se
   * referme, la ligne ouverte pouvant sortir de la liste filtree.
   */
  const go = useCallback(
    (next: AuditFilters, nextPage = 1): void => {
      void navigate({ pathname: AUDIT_PATH, search: writeFilters(next, nextPage).toString() });
    },
    [navigate],
  );

  const patch = (change: Partial<AuditFilters>): void => {
    go({ ...filters, ...change });
  };

  const exportJournal = (): void => {
    setExportState({ kind: 'busy' });

    const run = async (): Promise<void> => {
      try {
        const file = await createAdminApi(http).exportAuditEvents(toQuery(filters, new Date()));

        setExportState({ kind: 'done', filename: saveFile(file), truncated: exportIsTruncated(file.headers) });
      } catch (failure: unknown) {
        setExportState({ kind: 'error', message: messageOf(failure) });
      }
    };

    void run();
  };

  const items = result?.items ?? [];
  const first = items[0];
  const invalidFilters = error?.status === 422;

  const pageState = {
    total: result?.total ?? 0,
    page: result?.page ?? page,
    perPage: result?.perPage ?? PER_PAGE,
    itemCount: items.length,
  };

  // Quand un acteur ou une cible est filtre, toute ligne rendue le porte : la
  // premiere dit comment le nommer, sans stocker ni mettre une adresse dans l'URL.
  const actorChip = filters.actorUserId === '' ? null : (first?.actorEmail ?? 'compte choisi');
  const targetChip =
    filters.targetId === ''
      ? null
      : first === undefined
        ? 'cible choisie'
        : (describeTarget(first).label ?? targetTypeLabel(filters.targetType));

  const activeChips: { readonly key: string; readonly label: string; readonly clear: Partial<AuditFilters> }[] = [
    ...(actorChip === null ? [] : [{ key: 'actor', label: `Acteur : ${actorChip}`, clear: { actorUserId: '' } }]),
    ...(targetChip === null ? [] : [{ key: 'target', label: `Cible : ${targetChip}`, clear: { targetId: '' } }]),
    ...(filters.correlationId === ''
      ? []
      : [{ key: 'correlation', label: `Corrélation : ${shortId(filters.correlationId)}`, clear: { correlationId: '' } }]),
  ];

  const renderBody = (): ReactElement | null => {
    if (access.kind === 'loading') {
      return <LoadingState label="Vérification de votre accès à l’administration…" />;
    }

    if (access.kind === 'error') {
      return <ErrorState message={access.message} onRetry={reloadAccess} />;
    }

    if (access.kind === 'denied') {
      return (
        <AccessDenied title="Votre compte n’a pas accès à l’administration">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration : il ne peut donc pas lire le
          journal d’audit. Un rôle s’attribue depuis le serveur, jamais depuis cet écran.
        </AccessDenied>
      );
    }

    const list = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card title="Filtres" headingLevel="h2">
          <Box component="search" aria-label="Filtres du journal" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
              <SelectField
                label="Période"
                value={filters.period}
                options={PERIOD_OPTIONS}
                onChange={(value) => {
                  patch({ period: value as AuditPeriod });
                }}
              />
              <SelectField
                label="Action"
                value={toOption(filters.action)}
                options={withAny('Toutes les actions', ACTION_OPTIONS, filters.action, actionLabel)}
                onChange={(value) => {
                  patch({ action: fromOption(value) });
                }}
              />
              <SelectField
                label="Type de cible"
                value={toOption(filters.targetType)}
                options={withAny('Toutes les cibles', TARGET_TYPE_OPTIONS, filters.targetType, targetTypeLabel)}
                // Changer de type rend l'identifiant de cible sans objet : il n'a de sens qu'avec le sien.
                onChange={(value) => {
                  patch({ targetType: fromOption(value), targetId: '' });
                }}
              />
            </Box>

            {filters.period === 'custom' ? (
              <CustomPeriod
                key={`${filters.from}|${filters.to}`}
                from={filters.from}
                to={filters.to}
                onApply={(from, to) => {
                  patch({ from, to });
                }}
              />
            ) : null}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Switch
                label="Masquer les consultations"
                checked={filters.hideAccess}
                onChange={(checked) => {
                  patch({ hideAccess: checked });
                }}
              />
              {activeChips.map((chip) => (
                <Button
                  key={chip.key}
                  size="S"
                  icon={X}
                  aria-label={`Retirer le filtre « ${chip.label} »`}
                  onClick={() => {
                    patch(chip.clear);
                  }}
                >
                  {chip.label}
                </Button>
              ))}
              {hasActiveFilter(filters) ? (
                <Button
                  size="S"
                  variant="ghost"
                  onClick={() => {
                    go(DEFAULT_FILTERS);
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              ) : null}
            </Box>
          </Box>
        </Card>

        {error === null ? null : invalidFilters ? (
          <ErrorState
            title="Filtres invalides"
            message={INVALID_FILTERS_MESSAGE}
            retryLabel="Réinitialiser les filtres"
            onRetry={() => {
              go(DEFAULT_FILTERS);
            }}
          />
        ) : (
          <ErrorState message={error.message} onRetry={refresh} />
        )}

        {/* La liste d'une demande refusee n'est pas montree : elle se lirait comme le resultat des filtres. */}
        {invalidFilters ? null : (
          <Card>
            <Box role="status" sx={visuallyHidden}>
              {busy ? 'Chargement du journal…' : ''}
            </Box>
            {result === null && busy ? <LoadingState label="Chargement du journal…" /> : null}
            {result === null ? null : (
              <>
                <DataTable<AdminAuditEvent>
                  label="Événements du journal d’audit"
                  columns={detail.selectedId === null ? COLUMNS : COMPACT_COLUMNS}
                  rows={items}
                  getRowId={(event) => event.id}
                  onRowSelect={(event) => {
                    detail.open(event.id);
                  }}
                  selectedRowId={detail.selectedId}
                  emptyMessage={
                    // Une page au-delà de la dernière n'est pas un journal vide : le total le dit.
                    pageState.total > 0
                      ? 'Cette page est au-delà de la dernière : revenez en arrière pour retrouver des événements.'
                      : 'Aucun événement pour ces filtres.'
                  }
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Button
                    onClick={() => {
                      go(filters, Math.max(1, pageState.page - 1));
                    }}
                    disabled={pageState.page <= 1 || busy}
                  >
                    Page précédente
                  </Button>
                  <Typography sx={{ fontSize: 14, color: 'tokens.textMuted', textAlign: 'center' }}>
                    {describeAuditPage(pageState)}
                  </Typography>
                  <Button
                    onClick={() => {
                      go(filters, pageState.page + 1);
                    }}
                    disabled={!hasNextPage(pageState) || busy}
                  >
                    Page suivante
                  </Button>
                </Box>
              </>
            )}
          </Card>
        )}
      </Box>
    );

    return (
      <MasterDetail
        list={list}
        selectedId={detail.selectedId}
        detailLabel="Détail de l’événement"
        onClose={detail.close}
        renderDetail={(id) => {
          const event = items.find((candidate) => candidate.id === id);

          if (event !== undefined) {
            return <AuditEventDetail event={event} attempt={attempt} onFilter={patch} />;
          }

          return result === null && busy ? (
            <LoadingState label="Chargement de l’événement…" />
          ) : (
            <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>
              Cet événement n’est pas dans la page affichée : la page ou les filtres ont changé depuis l’ouverture du
              lien. Fermez ce volet pour revenir à la liste.
            </Typography>
          );
        }}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        eyebrow="Conformité"
        title="Journal d’audit"
        subtitle="Qui a fait quoi, quand, sur qui et pourquoi. En lecture seule, y compris pour un administrateur."
        actions={
          granted ? (
            <>
              <Button icon={RefreshCw} onClick={refresh} disabled={busy}>
                Actualiser
              </Button>
              {canExport ? (
                <Button icon={Download} onClick={exportJournal} disabled={exportState.kind === 'busy'}>
                  Exporter
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      <ExportStatus state={exportState} />
      {renderBody()}
    </Box>
  );
};
